import { useEffect, useRef, useState } from 'react';
import { chapters } from './data';
import { useActiveSection } from './hooks';
import Contact from './Contact';
import Experience from './Experience';
import Footer from './Footer';
import Hero from './Hero';
import Nav from './Nav';
import Recognition from './Recognition';
import SectionHead from './SectionHead';
import Work from './Work';

function useTheme() {
  const [theme, setTheme] = useState(() => {
    const current = document.documentElement.dataset.theme;
    return current === 'light' || current === 'dark' ? current : 'dark';
  });

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    try {
      localStorage.setItem('theme', theme);
    } catch {
      /* ignore */
    }
  }, [theme]);

  return {
    theme,
    toggle() {
      setTheme((current) => (current === 'dark' ? 'light' : 'dark'));
    },
  };
}

function ScrollProgress() {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    let frame = 0;
    const update = () => {
      const max = document.documentElement.scrollHeight - window.innerHeight;
      const progress = max > 0 ? Math.min(1, window.scrollY / max) : 0;
      el.style.transform = `scaleX(${progress})`;
    };
    const onScroll = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(update);
    };
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, []);

  return <div ref={ref} className="progress" aria-hidden="true" />;
}

function LazyDashboard() {
  const slot = useRef(null);
  const [View, setView] = useState(null);

  useEffect(() => {
    const el = slot.current;
    if (!el || process.env.NODE_ENV === 'test') return undefined;
    let cancel = false;
    const load = () => {
      import('./Dashboard').then((module) => {
        if (!cancel) setView(() => module.default);
      });
    };
    if (typeof IntersectionObserver === 'undefined') {
      load();
      return undefined;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        observer.disconnect();
        load();
      },
      { rootMargin: '360px 0px' }
    );
    observer.observe(el);
    return () => {
      cancel = true;
      observer.disconnect();
    };
  }, []);

  const Dash = View;

  return (
    <section className="section" id="dashboard" ref={slot}>
      <div className="wrap">
        <SectionHead
          index="04"
          label="GitHub"
          title="Public repositories"
          lede="Stars, languages, and a year of commits. Loaded when this section comes near."
        />
        {Dash ? <Dash /> : <div className="skeleton" aria-hidden="true" />}
      </div>
    </section>
  );
}

const CHAPTER_IDS = chapters.map((item) => item.id);

function Chapters() {
  const active = useActiveSection(CHAPTER_IDS);
  const current = active || 'top';

  return (
    <nav className="chapters" aria-label="On this page">
      {chapters.map((item) => (
        <a key={item.id} href={`#${item.id}`} aria-current={current === item.id ? 'true' : undefined}>
          <i />
          <span>{item.label}</span>
        </a>
      ))}
    </nav>
  );
}

export default function AppShell() {
  const { theme, toggle } = useTheme();

  return (
    <>
      <ScrollProgress />
      <Nav theme={theme} onToggleTheme={toggle} />
      <Chapters />
      <main>
        <Hero />
        <Work />
        <Experience />
        <Recognition />
        <LazyDashboard />
        <Contact />
      </main>
      <Footer />
    </>
  );
}
